import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, InputNumber, Select, Upload, Spin, Space } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, FileSearchOutlined } from '@ant-design/icons';
import { literatureService, LiteratureFormData } from '../../services/literature/literatureService';
import { categoryService, Category } from '../../services/literature/categoryService';
import { LiteratureLimsShell } from '../../components/literature/LiteratureLimsShell';
import RichTextEditor from '../../components/RichTextEditor';
import { coerceNotesToHtmlForEditor } from '../../utils/literatureNotesHtml';
import type { UploadFile } from 'antd';

const { TextArea } = Input;

const LiteratureForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const isEdit = !!id;

  useEffect(() => {
    const loadCategories = async () => {
      const list = await categoryService.getList();
      setCategories(list);
    };
    void loadCategories();
    if (isEdit) {
      fetchLiterature();
    }
  }, [id]);

  const fetchLiterature = async () => {
    if (!id) return;
    try {
      const literature = await literatureService.getDetail(id);
      form.setFieldsValue({
        ...literature,
        authors: literature.authors?.join(', '),
        keywords: literature.keywords?.join(', '),
        tags: literature.tags || [],
        categoryId: literature.categoryId || undefined,
        notes: coerceNotesToHtmlForEditor(literature.notes || '')
      });
    } catch (error: any) {
      message.error(error.message || '获取文献信息失败');
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const selectedCat = categories.find((c) => c._id === values.categoryId);
      const tagList: string[] = Array.isArray(values.tags)
        ? values.tags.map((t: string) => String(t).trim()).filter(Boolean)
        : values.tags
          ? String(values.tags)
              .split(',')
              .map((t: string) => t.trim())
              .filter(Boolean)
          : [];

      const formData: LiteratureFormData = {
        title: values.title,
        authors: values.authors ? values.authors.split(',').map((a: string) => a.trim()) : [],
        journal: values.journal,
        year: values.year,
        doi: values.doi,
        pmid: values.pmid,
        articleUrl: values.articleUrl != null ? String(values.articleUrl).trim() : '',
        volume: values.volume != null ? String(values.volume).trim() : '',
        issue: values.issue != null ? String(values.issue).trim() : '',
        pages: values.pages != null ? String(values.pages).trim() : '',
        publisher: values.publisher != null ? String(values.publisher).trim() : '',
        issn: values.issn != null ? String(values.issn).trim() : '',
        isbn: values.isbn != null ? String(values.isbn).trim() : '',
        docLanguage: values.docLanguage != null ? String(values.docLanguage).trim() : '',
        abstract: values.abstract,
        keywords: values.keywords ? values.keywords.split(',').map((k: string) => k.trim()) : [],
        category: selectedCat?.name || '',
        categoryId: values.categoryId || undefined,
        tags: tagList,
        notes: values.notes,
        readStatus: values.readStatus,
        pdf: !isEdit ? fileList[0]?.originFileObj : undefined
      };

      if (isEdit) {
        await literatureService.update(id!, formData);
        message.success('更新成功');
      } else {
        await literatureService.create(formData);
        message.success('创建成功');
      }
      navigate('/literature');
    } catch (error: any) {
      message.error(error.message || (isEdit ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
    
    // 如果是PDF文件且不是编辑模式，自动提取信息
    if (newFileList.length > 0 && !isEdit) {
      const file = newFileList[0].originFileObj;
      if (file && (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'))) {
        setExtracting(true);
        try {
          const extractedInfo = await literatureService.extractPDF(file);
          
          // 填充表单（只填充有值的字段）
          const formValues: any = {};
          if (extractedInfo.title) formValues.title = extractedInfo.title;
          if (extractedInfo.authors && extractedInfo.authors.length > 0) {
            formValues.authors = Array.isArray(extractedInfo.authors) 
              ? extractedInfo.authors.join(', ') 
              : extractedInfo.authors;
          }
          if (extractedInfo.journal) formValues.journal = extractedInfo.journal;
          if (extractedInfo.year) formValues.year = extractedInfo.year;
          if (extractedInfo.doi) formValues.doi = extractedInfo.doi;
          if (extractedInfo.pmid) formValues.pmid = extractedInfo.pmid;
          if (extractedInfo.abstract) formValues.abstract = extractedInfo.abstract;
          if (extractedInfo.keywords && extractedInfo.keywords.length > 0) {
            formValues.keywords = Array.isArray(extractedInfo.keywords)
              ? extractedInfo.keywords.join(', ')
              : extractedInfo.keywords;
          }
          
          if (Object.keys(formValues).length > 0) {
            form.setFieldsValue(formValues);
            message.success(`PDF信息提取成功，已自动填充 ${Object.keys(formValues).length} 个字段`);
          } else {
            message.warning('未能从PDF中提取到信息，请手动填写');
          }
        } catch (error: any) {
          console.error('PDF信息提取失败:', error);
          const errorMsg = error.message || error.message || 'PDF信息提取失败';
          message.warning(`${errorMsg}，请手动填写`);
        } finally {
          setExtracting(false);
        }
      }
    }
  };

  return (
    <LiteratureLimsShell
      crumbs={[
        { label: '首页', path: '/dashboard' },
        { label: '文献管理', path: '/literature' },
        { label: isEdit ? '编辑文献' : '添加文献' }
      ]}
      title={isEdit ? '编辑文献' : '添加文献'}
      subtitle="完善书目信息、附件与阅读状态；保存后在列表中检视与管理。"
      headActions={
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          className="lit-toolbar-btn"
          onClick={() => navigate('/literature')}
        >
          返回列表
        </Button>
      }
    >
      <div className="lit-form-panel">
        <Card className="lit-form-card" title="书目与附件" bordered={false}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ notes: '' }}
        >
          <div className="lit-form-block">
            <span className="lit-inspector-section-head">基本信息</span>
            <Form.Item
              name="title"
              label="标题"
              rules={[{ required: true, message: '请输入标题' }]}
            >
              <Input placeholder="文献标题" />
            </Form.Item>

            <Form.Item
              name="authors"
              label="作者"
              tooltip="多个作者用逗号分隔"
            >
              <Input placeholder="作者1, 作者2, 作者3" />
            </Form.Item>

            <Form.Item
              name="journal"
              label="期刊"
              tooltip="与 CSL 的 container-title 对应；RIS/BibTeX 导入时会从 container-title、journal、booktitle、event-title 等多条 CSL 字段归并到此。导出引用时写回 container-title。"
            >
              <Input placeholder="期刊 / 连续出版物或会议录等容器题名" />
            </Form.Item>

            <Form.Item name="year" label="年份">
              <InputNumber
                min={1900}
                max={new Date().getFullYear() + 1}
                style={{ width: '100%' }}
                placeholder="发表年份"
              />
            </Form.Item>
          </div>

          <div className="lit-form-block">
            <span className="lit-inspector-section-head">标识与链接</span>
            <Form.Item
              name="doi"
              label="DOI"
              extra={
                <p className="lit-form-hint">书目标识字段，仅用于检索与展示，不会自动进入「附件」列表。</p>
              }
            >
              <Input placeholder="DOI号" />
            </Form.Item>

            <Form.Item
              name="pmid"
              label="PMID"
              extra={
                <p className="lit-form-hint">书目标识字段，仅用于检索与展示，不会自动进入「附件」列表。</p>
              }
            >
              <Input placeholder="PMID号" />
            </Form.Item>

            <Form.Item
              name="articleUrl"
              label="文献网址"
              extra={
                <p className="lit-form-hint">
                  期刊或出版社提供的文章页、全文页等链接；属于书目信息，不会作为「附件」保存。其它需单独管理的链接可在保存后于文献详情「附件与文件」中添加。
                </p>
              }
            >
              <Input placeholder="https://…" />
            </Form.Item>
          </div>

          <div className="lit-form-block">
            <span className="lit-inspector-section-head">书目扩展</span>
            <p className="lit-form-hint" style={{ marginBottom: 14 }}>
              RIS/BibTeX 常见字段（卷期、页码、出版社、ISSN/ISBN、语言等）；从导入或数据库同步后会显示在此。
            </p>
            <Space wrap className="lit-form-inline-row" size="middle">
              <Form.Item name="volume" label="卷">
                <Input placeholder="Vol." />
              </Form.Item>
              <Form.Item name="issue" label="期">
                <Input placeholder="No." />
              </Form.Item>
              <Form.Item name="pages" label="页码">
                <Input placeholder="如 12–34" />
              </Form.Item>
            </Space>
            <Form.Item name="publisher" label="出版社">
              <Input placeholder="出版社" />
            </Form.Item>
            <Space wrap className="lit-form-inline-row" size="middle">
              <Form.Item name="issn" label="ISSN">
                <Input placeholder="期刊 ISSN" />
              </Form.Item>
              <Form.Item name="isbn" label="ISBN">
                <Input placeholder="图书 ISBN" />
              </Form.Item>
              <Form.Item name="docLanguage" label="语言">
                <Input placeholder="如 en、zh" />
              </Form.Item>
            </Space>
          </div>

          <div className="lit-form-block">
            <span className="lit-inspector-section-head">摘要与分类</span>
            <Form.Item name="abstract" label="摘要">
              <TextArea rows={4} placeholder="文献摘要" />
            </Form.Item>

            <Form.Item
              name="keywords"
              label="关键词"
              tooltip="多个关键词用逗号分隔"
            >
              <Input placeholder="关键词1, 关键词2, 关键词3" />
            </Form.Item>

            <Form.Item
              name="categoryId"
              label="集合（文件夹）"
              tooltip="在「文献列表 → 管理分类」中维护文件夹/集合"
            >
              <Select
                allowClear
                placeholder="选择集合，可不选"
                options={categories.map((c) => ({ label: c.name, value: c._id }))}
              />
            </Form.Item>

            <Form.Item name="tags" label="标签" tooltip="自由标签；列表搜索框可对标签做模糊匹配">
              <Select mode="tags" placeholder="输入后回车添加标签" tokenSeparators={[',']} />
            </Form.Item>
          </div>

          <div className="lit-form-block">
            <span className="lit-inspector-section-head">笔记与阅读状态</span>
            <Form.Item
              name="notes"
              label="笔记"
              tooltip="与 PDF 精读页、文献检视侧栏共用同一份笔记；支持富文本与插入图片链接"
              getValueFromEvent={(v) => v}
            >
              <RichTextEditor
                className="literature-notes-rich-editor literature-notes-rich-editor--form"
                variant="default"
                height={260}
                placeholder="个人笔记（支持加粗、列表、链接与颜色等）"
                showImage
              />
            </Form.Item>

            <Form.Item name="readStatus" label="阅读状态">
              <Select placeholder="选择阅读状态">
                <Select.Option value="未读">未读</Select.Option>
                <Select.Option value="阅读中">阅读中</Select.Option>
                <Select.Option value="已读">已读</Select.Option>
                <Select.Option value="精读">精读</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <div className="lit-form-block">
            <span className="lit-inspector-section-head">附件</span>
            {!isEdit ? (
              <Form.Item label="附件文件（可选）">
                <Upload
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  maxCount={1}
                  accept=".pdf,.doc,.docx"
                  disabled={extracting}
                >
                  <Button icon={<UploadOutlined />} disabled={extracting} className="lit-toolbar-btn">
                    {extracting ? '正在提取信息...' : '选择文件'}
                  </Button>
                </Upload>
                {extracting ? (
                  <p className="lit-form-hint" style={{ marginTop: 10 }}>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    正在从 PDF 中提取文献信息…
                  </p>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    <p className="lit-form-hint">
                      上传的文件会作为本条目的附件之一；保存后可在文献列表的检视器中继续上传更多附件。支持 PDF、DOC、DOCX，大小以服务端限制为准。
                    </p>
                    <span className="lit-form-hint lit-form-hint--accent">
                      <FileSearchOutlined aria-hidden />
                      上传 PDF 后将尝试自动识别文献信息
                    </span>
                  </div>
                )}
              </Form.Item>
            ) : (
              <Form.Item label="附件">
                <p className="lit-form-hint">
                  文件请在文献列表中打开检视器，在「附件与文件」中上传与管理（PDF 与其它附件并列，不再单独区分主文件）。
                </p>
              </Form.Item>
            )}
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <div className="lit-form-actions">
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? '更新' : '创建'}
              </Button>
              <Button type="default" className="lit-toolbar-btn" onClick={() => navigate('/literature')}>
                取消
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
      </div>
    </LiteratureLimsShell>
  );
};

export default LiteratureForm;

